import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que recopilamos',
    body: 'Recopilamos la información que proporcionás al realizar tu compra: nombre, dirección de correo electrónico y datos necesarios para procesar el pago. No almacenamos información de tarjetas de crédito en nuestros servidores; ese proceso lo maneja directamente la pasarela de pagos.'
  }, {
    heading: '2. Uso de la información',
    body: 'Usamos tu información exclusivamente para entregarte el producto digital adquirido, enviarte el acceso por correo electrónico y notificarte sobre actualizaciones relevantes del material. Nunca venderemos, alquilaremos ni compartiremos tu información con terceros con fines comerciales.'
  }, {
    heading: '3. Cookies y tecnologías similares',
    body: 'Nuestro sitio puede utilizar cookies funcionales para mejorar tu experiencia de navegación y herramientas analíticas para entender el rendimiento de la página. Podés desactivar las cookies desde la configuración de tu navegador en cualquier momento.'
  }, {
    heading: '4. Almacenamiento y seguridad',
    body: 'Tus datos se almacenan en servidores seguros con protocolos de cifrado estándar de la industria. Implementamos medidas técnicas y organizativas razonables para protegerlos contra accesos no autorizados, pérdida o alteración.'
  }, {
    heading: '5. Tus derechos',
    body: 'Tenés derecho a acceder, rectificar, actualizar o solicitar la eliminación de tus datos personales en cualquier momento. También podés oponerte al tratamiento de tus datos o retirar tu consentimiento.'
  }, {
    heading: '6. Cambios en esta política',
    body: 'Podemos actualizar esta política de privacidad ocasionalmente. La versión más reciente siempre estará disponible en esta página, con su fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Naturaleza del producto',
    body: 'El "Botiquín de Emergencia Post-Oficina" es un producto digital descargable en formato PDF. Al completar la compra, recibirás acceso inmediato al material a través de un enlace de descarga enviado al correo electrónico que indicaste.'
  }, {
    heading: '2. Licencia de uso',
    body: 'La compra te otorga una licencia personal, no transferible y no exclusiva para uso individual del contenido. No está permitido revender, redistribuir, copiar, reproducir total o parcialmente, ni compartir el material con terceros bajo ninguna modalidad.'
  }, {
    heading: '3. Política de reembolso',
    body: 'Por tratarse de un producto digital de acceso inmediato y descarga, no se realizan reembolsos una vez efectuada la compra. Te recomendamos leer atentamente la descripción del producto antes de realizar tu pedido.'
  }, {
    heading: '4. Limitación de responsabilidad',
    body: 'Este producto contiene material de desarrollo personal y bienestar energético. No reemplaza ni sustituye tratamiento médico, psicológico, psiquiátrico ni terapéutico profesional. Los resultados pueden variar según el compromiso y la práctica individual de cada persona.'
  }, {
    heading: '5. Propiedad intelectual',
    body: 'Todo el contenido del botiquín (textos, protocolos, gráficos, diseño) está protegido por derechos de autor y es propiedad exclusiva de la autora. Su reproducción, distribución o uso comercial sin autorización expresa por escrito está prohibida.'
  }, {
    heading: '6. Modificaciones',
    body: 'Nos reservamos el derecho de modificar el contenido, precios y condiciones del producto en cualquier momento. Los cambios no afectarán retroactivamente a compras ya realizadas.'
  }, {
    heading: '7. Jurisdicción',
    body: 'Estos términos se rigen por las leyes de la República Argentina. Cualquier controversia derivada del uso del producto se resolverá ante los tribunales competentes de la Ciudad Autónoma de Buenos Aires.'
  }]
};
export const Footer = () => {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal]);
  // Close on ESC
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);
  const content = openModal === 'privacy' ? privacyContent : openModal === 'terms' ? termsContent : null;
  return <>
      <footer className="py-12 border-t border-gold/10 bg-navy-900 text-gray-500 text-sm font-light" data-id="element-62">
        <div className="max-w-6xl mx-auto px-6" data-id="element-63">
          <div className="flex flex-col items-center gap-6" data-id="element-64">
            <div className="flex items-center gap-2 text-gold/70" data-id="element-65">
              <Shield size={18} strokeWidth={1.5} data-id="element-66" />
              <span className="font-serif text-base tracking-wide" data-id="element-67">
                Botiquín de Emergencia Post-Oficina
              </span>
            </div>

            <div className="w-16 h-px bg-gold/20" data-id="element-68" />

            <nav className="flex flex-col sm:flex-row items-center gap-3 sm:gap-8" data-id="element-69">
              <button onClick={() => setOpenModal('privacy')} className="text-gray-400 hover:text-gold transition-colors duration-200 tracking-wide" data-id="element-70">
                Política de Privacidad
              </button>
              <span className="hidden sm:block w-1 h-1 rounded-full bg-gold/30" data-id="element-71" />
              <button onClick={() => setOpenModal('terms')} className="text-gray-400 hover:text-gold transition-colors duration-200 tracking-wide" data-id="element-72">
                Términos y Condiciones
              </button>
            </nav>

            <div className="text-center mt-4 space-y-2" data-id="element-73">
              <p data-id="element-74">
                © {new Date().getFullYear()} Botiquín de Emergencia
                Post-Oficina. Todos los derechos reservados.
              </p>
              <p className="text-xs opacity-60 max-w-md" data-id="element-75">
                Este producto no reemplaza la terapia psicológica ni el
                tratamiento médico.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-76">
        {openModal && content && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" onClick={() => setOpenModal(null)} role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-77">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-navy-900/90 backdrop-blur-sm" data-id="element-78" />

            {/* Modal content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl max-h-[85vh] bg-navy-800 border border-gold/30 rounded-sm shadow-[0_0_60px_rgba(201,169,97,0.1)] flex flex-col" data-id="element-79">
              {/* Decorative corners */}
              <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-gold/40 pointer-events-none" data-id="element-80" />
              <div className="absolute top-3 right-3 w-6 h-6 border-t border-r border-gold/40 pointer-events-none" data-id="element-81" />
              <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-gold/40 pointer-events-none" data-id="element-82" />
              <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-gold/40 pointer-events-none" data-id="element-83" />

              {/* Header */}
              <div className="flex items-center justify-between px-6 md:px-10 py-6 border-b border-gold/20 shrink-0" data-id="element-84">
                <div className="flex items-center gap-3" data-id="element-85">
                  <Shield className="text-gold" size={22} strokeWidth={1.5} data-id="element-86" />
                  <h2 id="modal-title" className="font-serif text-xl md:text-2xl text-offwhite" data-id="element-87">
                    {content.title}
                  </h2>
                </div>
                <button onClick={() => setOpenModal(null)} aria-label="Cerrar" className="p-2 rounded-full text-gray-400 hover:text-gold hover:bg-gold/10 transition-colors" data-id="element-88">
                  <X size={20} data-id="element-89" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="overflow-y-auto px-6 md:px-10 py-8 space-y-6 custom-scroll" data-id="element-90">
                {content.sections.map((section, i) => <article key={i} data-id="element-91">
                    <h3 className="font-serif text-lg text-gold mb-2" data-id="element-92">
                      {section.heading}
                    </h3>
                    <p className="text-gray-300 font-light leading-relaxed text-[15px]" data-id="element-93">
                      {section.body}
                    </p>
                  </article>)}
                <p className="text-xs text-gray-500 italic pt-4 border-t border-gold/10" data-id="element-94">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-AR', {
                year: 'numeric',
                month: 'long'
              })}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};